<template>
  <el-alert
    v-if="notice"
    class="ai-credits-banner"
    :type="notice.type"
    :title="notice.title"
    :description="notice.text"
    show-icon
    @close="dismiss"
  />
</template>

<script>
import aiCreditsQuery from '@/graphql/segmentation/ai/aiCreditsQuery.gql';

const STORAGE_KEY = 'segmentation-credits-banner';

/**
 * A warning across the top of the dashboard when the station's AI credits
 * are running low, used up or paused, so it is not a surprise when an AI
 * action stops working.
 *
 * It uses the service's own status (the same one the AI usage window shows),
 * and a closed warning stays closed for the rest of the session unless the
 * status gets worse.
 */
export default {
  name: 'AiCreditsBanner',
  data: () => ({ credits: null, dismissed: '' }),
  computed: {
    status() {
      return (this.credits && this.credits.status) || null;
    },
    remaining() {
      return Math.max(0, Math.round(Number(this.credits && this.credits.credits_remaining) || 0));
    },
    notice() {
      const key = this.status && this.status.key;
      if (!key || key === this.dismissed) {
        return null;
      }
      if (key === 'low') {
        return {
          type: 'warning',
          title: 'AI credits are running low',
          text: `${this.remaining.toLocaleString()} credits left. Contact Colony to top up before they run out.`,
        };
      }
      if (key === 'exhausted') {
        return {
          type: 'error',
          title: 'AI credits are used up',
          text: 'AI features are unavailable until Colony tops up your credits.',
        };
      }
      if (key === 'paused') {
        return {
          type: 'error',
          title: 'AI is paused',
          text: this.status.reason || 'AI usage is paused for this station.',
        };
      }
      return null;
    },
  },
  created() {
    this.dismissed = this.readDismissed();
    this.load();
  },
  methods: {
    /**
     * Note: Informational only. If the balance cannot be loaded, no banner
     * shows.
     */
    async load() {
      try {
        const { data } = await this.$apollo.query({
          client: 'apiv2',
          query: aiCreditsQuery,
          fetchPolicy: 'network-only',
        });
        this.credits = data.aiCredits;
      } catch (e) {
        this.credits = null;
      }
    },

    /**
     * Note: Browser storage can be blocked, so a failure just means the
     * warning comes back on the next visit.
     */
    readDismissed() {
      try {
        return window.sessionStorage.getItem(STORAGE_KEY) || '';
      } catch (e) {
        return '';
      }
    },

    dismiss() {
      this.dismissed = this.status ? this.status.key : '';
      try {
        window.sessionStorage.setItem(STORAGE_KEY, this.dismissed);
      } catch (e) {
        /* Ignored on purpose: see the Note above readDismissed. */
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.ai-credits-banner {
  margin-bottom: 14px;
}
</style>



// WEBPACK FOOTER //
// AiCreditsBanner.vue?7cf42a25